/**
 * BoardManagerAdapter — drop-in replacement for the legacy game/BoardManager.js
 * 
 * Exposes the same API the Vue components expect (load, export, update, addBuilding,
 * upgradeBuilding, etc.) but delegates the simulation to @vortex/engine.
 */

import { GameEngine, allBuildings } from '@vortex/engine'
import { resources as engineResources } from '@vortex/engine'

// Export resources list for UI components (replaces legacy game/config/resources.js)
export const resources = engineResources.map(r => ({ name: r.name, icon: r.icon || r.name }))

// Technologies (from legacy game/config/technologies.js)
const technologies = [
  { id: 'hull-1', name: 'Hull (Level 1)', requiredTechnology: 'stone', description: 'Expand your hull for more building space.', costs: { energy: 20, stone: 20 } },
  { id: 'hull-2', name: 'Hull (Level 2)', requiredTechnology: 'hull-1', description: 'Further expand your hull.', costs: { energy: 60, stone: 20, 'iron-ore': 20 } },
  { id: 'hull-3', name: 'Hull (Level 3)', requiredTechnology: 'hull-2', description: 'Further expand your hull.', costs: { energy: 500, stone: 500, iron: 1000 } },
  { id: 'hull-4', name: 'Hull (Level 4)', requiredTechnology: 'hull-3', description: 'Further expand your hull.', costs: { energy: 1000, stone: 1000, iron: 3000, copper: 3000 } },
  { id: 'hull-5', name: 'Hull (Level 5)', requiredTechnology: 'hull-4', description: 'Further expand your hull.', costs: { energy: 3000, stone: 5000, iron: 10000, copper: 10000 } },
  { id: 'hull-6', name: 'Hull (Level 6)', requiredTechnology: 'hull-5', description: 'Further expand your hull.', costs: { energy: 5000, stone: 15000, iron: 20000, copper: 20000, gold: 5000 } },
  { id: 'hull-7', name: 'Hull (Level 7)', requiredTechnology: 'hull-6', description: 'Further expand your hull.', costs: { energy: 7000, stone: 22000, iron: 30000, copper: 30000, gold: 20000 } },
  { id: 'stone', name: 'Stone', description: 'Discover stone extraction.', costs: { energy: 30 } },
  { id: 'ores', name: 'Ore Extraction', requiredTechnology: ['stone', 'hull-1'], description: 'Extract ores from the asteroid.', costs: { energy: 30, stone: 5 } },
  { id: 'iron', name: 'Iron', requiredTechnology: ['stone', 'ores', 'hull-1'], description: 'Smelt iron from ore.', costs: { energy: 40, 'iron-ore': 50 } },
  { id: 'copper', name: 'Copper', requiredTechnology: 'iron', description: 'Smelt copper from ore.', costs: { energy: 40, 'iron-ore': 50, 'copper-ore': 50 } },
  { id: 'gold', name: 'Gold', requiredTechnology: 'copper', description: 'Refine gold.', costs: { energy: 300, stone: 2000, iron: 1000 } },
]

// Hull blueprint (from legacy BoardManager)
const hullBlueprint = [
  "    3223    ",
  "   321123   ",
  "   210012   ",
  "  32000023  ",
  "433244442334",
  "455567765554",
  " 56 7777 65 ",
  " 67      76 ",
]

function transformBlueprintIntoHull(level) {
  const hull = []
  for (let i in hullBlueprint) {
    const chars = hullBlueprint[i].split('')
    for (let j in chars) {
      if (parseInt(chars[j]) <= level) {
        hull.push(`${i}_${j}`)
      }
    }
  }
  return hull
}

export class BoardManager {
  constructor() {
    this.engine = new GameEngine()
    this.boardId = 'starship'
    this._tick = 0
    this._buildings = []
    this.researches = {}
    this.config = {
      name: 'Basic Starship',
      level: 0,
      hull: transformBlueprintIntoHull(0),
    }
  }

  reset() {
    this.engine = new GameEngine()
    this._tick = 0
    this._buildings = []
    this.researches = {}
    this.config = {
      name: 'Basic Starship',
      level: 0,
      hull: transformBlueprintIntoHull(0),
    }
  }

  updateHullLevel(level) {
    this.config.level = level
    this.config.hull = transformBlueprintIntoHull(level)
  }

  // === Load / Export ===

  load(config) {
    this.config = {
      name: config.name || 'Basic Starship',
      level: config.level || 0,
      hull: config.hull || transformBlueprintIntoHull(config.level || 0),
    }
    this._tick = config.tick || 0
    this.researches = config.researches || {}
    
    // Create board in engine
    this.engine = new GameEngine()
    this.engine.createBoard(this.boardId, 12, 8)
    
    // Replay building placements
    this._buildings = []
    if (config.buildings) {
      for (const b of config.buildings) {
        const [x, y] = b.position.split('_').map(Number)
        this.engine.emit(this.boardId, 'BUILDING_PLACED', {
          buildingName: b.name,
          x,
          y,
          level: b.level || 0,
        })
        // Restore state
        const placed = this.engine.getBoardState(this.boardId)
        const lastBuilding = placed && placed.buildings[placed.buildings.length - 1]
        if (lastBuilding) {
          if (b.isEnabled === false) {
            this.engine.emit(this.boardId, 'BUILDING_TOGGLED', { buildingId: lastBuilding.id })
          }
          // Restore contains
          lastBuilding.contains = b.contains || {}
        }
      }
    }
    
    // Advance to saved tick
    if (this._tick > 0) {
      this.engine.advanceToTick(this._tick)
    }
    
    this._syncBuildings()
  }

  export() {
    return {
      name: this.config.name,
      level: this.config.level,
      hull: this.config.hull,
      tick: this._tick,
      buildings: this._buildings.map(b => ({
        name: b.name,
        level: b.level,
        position: b.position,
        isEnabled: b.isEnabled,
        contains: b.contains,
        status: b.status,
        config: b.config,
        buildingActive: b.buildingActive,
        buildingRemaining: b.buildingRemaining,
      })),
      inventory: this.inventory.contents,
      storage: this.inventory.storage,
      researches: this.researches,
    }
  }

  // === Internal sync: pull building data from engine state ===

  _syncBuildings() {
    const state = this.engine.getBoardState(this.boardId)
    if (!state || !state.buildings) {
      this._buildings = []
      return
    }
    
    this._buildings = state.buildings.map(b => {
      const config = this._getBuildingConfig(b.name, b.level)
      return {
        id: b.id,
        name: b.name,
        level: b.level,
        position: `${b.x}_${b.y}`,
        isEnabled: b.isEnabled,
        contains: b.contains,
        status: 'AVAILABLE',
        config: config,
        buildingActive: false,
        buildingRemaining: 0,
      }
    })
  }

  _getBuildingConfig(name, level) {
    const bc = allBuildings[name]
    if (!bc) return null
    const lc = bc.levels[level]
    if (!lc) return null
    return {
      name: bc.name,
      fullname: bc.fullname,
      description: bc.description,
      type: bc.type,
      level: level,
      levelMax: bc.levelMax,
      canBeDisabled: bc.canBeDisabled,
      requiredTechnology: bc.requiredTechnology,
      time: lc.time,
      constructionCosts: lc.constructionCosts,
      costs: lc.costs,
      provides: lc.provides,
      stores: lc.stores,
    }
  }

  // === Buildings ===

  get buildings() {
    return this._buildings
  }

  set buildings(buildings) {
    this._buildings = buildings
  }

  getBuildingByPosition(position) {
    return this._buildings.find(x => x.position === position)
  }

  addBuilding(building, position) {
    const [x, y] = position.split('_').map(Number)
    const name = (building.config && building.config.name) || building.name
    const level = (building.config && building.config.level) || building.level || 0
    this.engine.emit(this.boardId, 'BUILDING_PLACED', { buildingName: name, x, y, level })
    this._syncBuildings()
  }

  removeBuildingByPosition(position) {
    const building = this.getBuildingByPosition(position)
    if (building) {
      this.engine.emit(this.boardId, 'BUILDING_DEMOLISHED', { buildingId: building.id })
      this._syncBuildings()
    }
  }

  moveBuildings(fromPosition, toPosition) {
    const from = this.getBuildingByPosition(fromPosition)
    const to = this.getBuildingByPosition(toPosition)
    if (from) {
      // Demolish + replace at new position
      const [nx, ny] = toPosition.split('_').map(Number)
      this.engine.emit(this.boardId, 'BUILDING_DEMOLISHED', { buildingId: from.id })
      this.engine.emit(this.boardId, 'BUILDING_PLACED', { buildingName: from.name, x: nx, y: ny, level: from.level })
    }
    if (to) {
      const [ox, oy] = fromPosition.split('_').map(Number)
      this.engine.emit(this.boardId, 'BUILDING_DEMOLISHED', { buildingId: to.id })
      this.engine.emit(this.boardId, 'BUILDING_PLACED', { buildingName: to.name, x: ox, y: oy, level: to.level })
    }
    this._syncBuildings()
  }

  upgradeBuildingByPosition(position) {
    const building = this.getBuildingByPosition(position)
    if (!building) return null
    
    const newLevel = building.level + 1
    const config = allBuildings[building.name]
    if (!config || newLevel > config.levelMax) return null
    
    const newLevelConfig = config.levels[newLevel]
    if (!this.inventory.canRetrieve(newLevelConfig.constructionCosts)) {
      throw new Error('Unable to find resources for upgrade')
    }
    
    this.inventory.retrieve(newLevelConfig.constructionCosts)
    this.engine.emit(this.boardId, 'BUILDING_UPGRADED', { buildingId: building.id, newLevel })
    this._syncBuildings()
    return this.getBuildingByPosition(position)
  }

  forceUpgradeBuildingByPosition(position) {
    const building = this.getBuildingByPosition(position)
    if (!building) return null
    this.engine.emit(this.boardId, 'BUILDING_UPGRADED', { buildingId: building.id, newLevel: building.level + 1 })
    this._syncBuildings()
    return this.getBuildingByPosition(position)
  }

  upgradableByPosition(position) {
    const building = this.getBuildingByPosition(position)
    if (!building || building.level >= building.config.levelMax) return null
    const config = allBuildings[building.name]
    if (!config) return null
    const newLevelConfig = config.levels[building.level + 1]
    if (!newLevelConfig) return null
    return {
      config: this._getBuildingConfig(building.name, building.level + 1),
    }
  }

  enableBuilding(position) {
    const building = this.getBuildingByPosition(position)
    if (building && !building.isEnabled) {
      this.engine.emit(this.boardId, 'BUILDING_TOGGLED', { buildingId: building.id })
      this._syncBuildings()
    }
  }

  disableBuilding(position) {
    const building = this.getBuildingByPosition(position)
    if (building && building.isEnabled) {
      this.engine.emit(this.boardId, 'BUILDING_TOGGLED', { buildingId: building.id })
      this._syncBuildings()
    }
  }

  // === Inventory (computed from buildings) ===

  get inventory() {
    const self = this
    return {
      get contents() {
        const result = {}
        for (const b of self._buildings) {
          for (const [res, amt] of Object.entries(b.contains || {})) {
            result[res] = (result[res] || 0) + amt
          }
        }
        return result
      },
      get storage() {
        const total = {}
        for (const b of self._buildings) {
          for (const [res, cap] of Object.entries(b.config && b.config.stores || {})) {
            total[res] = (total[res] || 0) + cap
          }
        }
        const contents = this.contents
        for (const [res, amt] of Object.entries(contents)) {
          if (total[res]) total[res] -= amt
        }
        return total
      },
      canRetrieve(wants) {
        const contents = this.contents
        for (const [res, amt] of Object.entries(wants)) {
          if (!contents[res] || contents[res] < amt) return false
        }
        return true
      },
      retrieve(wants) {
        for (const [res, amt] of Object.entries(wants)) {
          let remaining = amt
          for (const b of self._buildings) {
            if (remaining <= 0) break
            if (b.contains[res] > 0) {
              const take = Math.min(b.contains[res], remaining)
              b.contains[res] -= take
              remaining -= take
            }
          }
        }
      },
      store(resources) {
        for (const [res, amt] of Object.entries(resources)) {
          let remaining = amt
          for (const b of self._buildings) {
            if (remaining <= 0) break
            const cap = b.config && (b.config && b.config.stores && b.config.stores[res]) || 0
            const space = cap - (b.contains[res] || 0)
            if (space > 0) {
              const add = Math.min(space, remaining)
              b.contains[res] = (b.contains[res] || 0) + add
              remaining -= add
            }
          }
        }
      },
      canStoreAny(resources) {
        const storage = this.storage
        for (const [res] of Object.entries(resources)) {
          if (storage[res] && storage[res] > 0) return true
        }
        return false
      },
    }
  }

  // === Tick / Update ===

  update() {
    // Advance one tick in the engine
    this.engine.advanceOneTick()
    this._tick = this.engine.tick
    this._syncBuildings()
  }

  // === Research ===

  availableResearches() {
    return technologies.filter(x => !this.researched(x.id))
  }

  researched(technologyId) {
    if (Array.isArray(technologyId)) {
      return technologyId.every(x => this.researches[x] === true)
    }
    return this.researches[technologyId] === true
  }

  canResearch(technologyId) {
    if (this.researched(technologyId)) return false
    const research = technologies.find(x => x.id === technologyId)
    if (!research) return false
    return this.inventory.canRetrieve(research.costs)
  }

  research(technologyId) {
    if (this.researched(technologyId)) return false
    const research = technologies.find(x => x.id === technologyId)
    if (!research) throw new Error(`Unable to find research: ${technologyId}`)
    if (this.inventory.canRetrieve(research.costs)) {
      this.inventory.retrieve(research.costs)
      this.researches[technologyId] = true
      this._afterResearch(technologyId)
      return true
    }
    return false
  }

  forceResearch(technologyId) {
    if (!this.researched(technologyId)) {
      this.researches[technologyId] = true
      this._afterResearch(technologyId)
      return true
    }
    return false
  }

  _afterResearch(technologyId) {
    const hullMatch = technologyId.match(/^hull-(\d+)$/)
    if (hullMatch) {
      this.updateHullLevel(parseInt(hullMatch[1]))
    }
  }

  // === Available buildings ===

  availableBuildings() {
    const result = []
    for (const [name, config] of Object.entries(allBuildings)) {
      if (config.levels[0]) {
        const buildingConfig = this._getBuildingConfig(name, 0)
        result.push({
          config: buildingConfig,
          name,
          level: 0,
        })
      }
    }
    return result
  }

  canConstruct(name, level) {
    const config = allBuildings[name]
    if (!config || !config.levels[level]) return false
    return this.inventory.canRetrieve(config.levels[level].constructionCosts)
  }
}

// === BuildingFactory adapter ===

export class BuildingFactory {
  getBuilding(name, level) {
    const config = allBuildings[name]
    if (!config || !config.levels[level]) return null
    return {
      config: {
        name: config.name,
        fullname: config.fullname,
        description: config.description,
        type: config.type,
        level,
        levelMax: config.levelMax,
        canBeDisabled: config.canBeDisabled,
        requiredTechnology: config.requiredTechnology,
        ...config.levels[level],
      },
      name,
      level,
    }
  }

  getAllBuildingMockups() {
    const result = []
    for (const [name, config] of Object.entries(allBuildings)) {
      if (config.levels[0]) {
        result.push({
          config: {
            name: config.name,
            fullname: config.fullname,
            description: config.description,
            type: config.type,
            level: 0,
            levelMax: config.levelMax,
            canBeDisabled: config.canBeDisabled,
            requiredTechnology: config.requiredTechnology,
            ...config.levels[0],
          },
          name,
          level: 0,
        })
      }
    }
    return result
  }

  clone(info) {
    return { ...info }
  }
}

export default BoardManager
/* cjs engine fix 1784750614773663504 */



// WEBPACK FOOTER //
// ./src/engine/BoardManagerAdapter.js